import { compose, withStateHandlers, lifecycle, withProps } from 'recompose'
import { connect } from 'react-redux'
import { SERVICE_CATEGORY_CONTENT_ID, SERVICE_CATEGORY_CTA_WRAPPER_ID } from '../../constants'
import { toggleStoreDetailsBottomBanner } from 'helpers/custom-events'
import ServiceCategoryPage from './ServiceCategoryPage'
import {
  getCurrentStoreIsE2EAppointment,
  getCurrentStore
} from 'selectors/getCurrentStore'
import {
  REQUEST_APPOINTMENT,
  SCHEDULE_APPOINTMENT,
  STATES_OBJ
} from 'constant'
import { getDescription, getCanonicalUrl } from 'components/Head/helpers'
import {
  formatPhoneNumber,
  generateServiceCategorySchema,
  generateServiceSchema,
  removeLastSegment
} from '../../helpers'
import { getStoreDetails } from 'components/StoreDetailsPage/components/StoreInformation/selectors.js'

const mapStateToProps = (state) => {
  const { app: { data }} = state
  const activeStore = getCurrentStore(state)
  const currentStore = activeStore || getStoreDetails(state)
  const {
    address1,
    city,
    state: storeState,
    zipcode,
    latitude,
    longitude,
    phoneNumber
  } = activeStore ? activeStore.address : currentStore.storeAddress
  const { email } = currentStore.owner
  const storeNumber = activeStore ? activeStore.storeNumber : currentStore.storeNum
  const storePhoneNumber = formatPhoneNumber(phoneNumber)
  let currentUrl = ''

  if (window && window.location) {
    currentUrl = window.location.href
  }

  return {
    metaDescription: data ? getDescription(data) : '',
    canonicalUrl: data.canonical ? getCanonicalUrl(data) : currentUrl,
    storeNumber,
    storeAddress: `${address1}, ${city}, ${storeState} ${zipcode}`,
    storeCity: city,
    storeState,
    storeStateFull: STATES_OBJ[storeState],
    parentStoreUrl: removeLastSegment(currentUrl),
    storeZip: zipcode,
    storeEmail: email,
    storePhoneNumber,
    storeLatitude: latitude,
    storeLongitude: longitude,
    isE2EAppointment: getCurrentStoreIsE2EAppointment(state)
  }
}

const withFloatingCTAsStateHandlers = withStateHandlers({
  intersectionObserver: null,
  inContentSection: false,
  areCTAsVisible: false
},
{
  setObserver: () => intersectionObserver => ({ intersectionObserver }),
  setInContentSection: () => inContentSection => ({ inContentSection }),
  setAreCTAsVisible: () => areCTAsVisible => ({ areCTAsVisible })
})

const withAppointmentButtonProps = withProps(({
  isE2EAppointment
}) => ({
  appointmentBtnTitle: isE2EAppointment ? SCHEDULE_APPOINTMENT : REQUEST_APPOINTMENT
}))

const withEnhancedProps = withProps(({ inContentSection, areCTAsVisible }) => ({
  displayFloatingCTAs: inContentSection && !areCTAsVisible
}))

const withObserverLifecycle = lifecycle({
  componentDidMount() {
    const {
      setObserver,
      setInContentSection,
      setAreCTAsVisible
    } = this.props
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    }
    const intersectionObserver = new IntersectionObserver(entries => {
      const ctaEntries = entries.filter(({ target }) => target.id === SERVICE_CATEGORY_CTA_WRAPPER_ID)
      const contentEntries = entries.filter(({ target }) => target.id === SERVICE_CATEGORY_CONTENT_ID)
      contentEntries.length && setInContentSection(contentEntries.every(entry => entry.isIntersecting))
      ctaEntries.length && setAreCTAsVisible(ctaEntries.every(entry => entry.isIntersecting))
    }, observerOptions)
    intersectionObserver.observe(document.getElementById(SERVICE_CATEGORY_CONTENT_ID))
    intersectionObserver.observe(document.getElementById(SERVICE_CATEGORY_CTA_WRAPPER_ID))
    setObserver(intersectionObserver)
  },

  componentDidUpdate({ displayFloatingCTAs: prevDisplayFloatingCTAs }) {
    const { displayFloatingCTAs } = this.props
    displayFloatingCTAs !== prevDisplayFloatingCTAs
      && toggleStoreDetailsBottomBanner({ detail: { isOpen: displayFloatingCTAs }})
  },
  componentWillUnmount() {
    const { intersectionObserver } = this.props
    intersectionObserver.disconnect()
  }
})

const withServiceCategorySchemasProps = withProps(({
  storeCity,
  categoryId,
  categoryName,
  metaDescription,
  parentStoreUrl,
  canonicalUrl,
  storeNumber,
  storeEmail,
  storePhoneNumber,
  storeAddress,
  storeState,
  storeZip,
  storeStateFull,
  storeLatitude,
  storeLongitude
}) => {
  const serviceSchema = generateServiceSchema({ storeCity, categoryId, categoryName, metaDescription })
  const serviceCategorySchema = generateServiceCategorySchema({
    parentStoreUrl,
    canonicalUrl,
    storeCity,
    storeNumber,
    metaDescription,
    storeEmail,
    storePhoneNumber,
    storeAddress,
    storeState,
    storeZip,
    storeStateFull,
    storeLatitude,
    storeLongitude
  })

  return {
    serviceSchema,
    serviceCategorySchema
  }
})

export default compose(
  connect(mapStateToProps),
  withFloatingCTAsStateHandlers,
  withEnhancedProps,
  withObserverLifecycle,
  withAppointmentButtonProps,
  withServiceCategorySchemasProps
)(ServiceCategoryPage)
